import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Spinner } from "@heroui/react";
import { ToastContainer, toast } from "react-toastify";
import useErrorHandling from "@/hooks/useErrorHandling";
import { api } from "@/api";
import { InvitationAuthBlock } from "@/app/components/invitation/InvitationAuthBlock";
import { ExistingRenterFlow } from "@/app/components/invitation/ExistingRenterFlow";
import { NewRenterFlow } from "@/app/components/invitation/NewRenterFlow";
import {
  InvitationResponse,
  NewRenterAcceptCallback,
  ExistingRenterAcceptCallback,
} from "@/app/components/invitation/types";

export function InvitationPage() {
  const { opaque_token } = useParams<{ opaque_token: string }>();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [invitationData, setInvitationData] =
    useState<InvitationResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(() => Boolean(localStorage.getItem("access_token")));
  const handleError = useErrorHandling();

  useEffect(() => {
    if (!opaque_token) {
      setError("Токен приглашения не указан");
      setIsLoading(false);
      return;
    }

    if (!isAuthorized) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    api.invitations
      .get(opaque_token)
      .then((response) => {
        setInvitationData(response.data);
        setError(null);
      })
      .catch((err) => {
        if (err?.status === 401) {
          localStorage.removeItem("access_token");
          setIsAuthorized(false);
          setError(null);
          return;
        }

        const { errorMessage } = handleError(err);
        setError(errorMessage);
        toast.error(errorMessage);
        console.error("Failed to load invitation:", err);
        setIsLoading(false);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [opaque_token, isAuthorized, handleError]);

  const handleNewRenterAccept: NewRenterAcceptCallback = (data) => {
    if (!opaque_token) return;

    setIsSubmitting(true);
    api.invitations
      .acceptNewRenter(opaque_token, data)
      .then(() => {
        toast.success("Приглашение успешно принято");
        navigate("/dashboard");
      })
      .catch((err) => {
        if (err?.status === 401) {
          localStorage.removeItem("access_token");
          setIsAuthorized(false);
          return;
        }
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
        console.error("Failed to accept invitation:", err);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  const handleExistingRenterAccept: ExistingRenterAcceptCallback = () => {
    if (!opaque_token) return;

    setIsSubmitting(true);
    api.invitations
      .acceptExistingRenter(opaque_token)
      .then(() => {
        toast.success("Приглашение успешно принято");
        navigate("/dashboard");
      })
      .catch((err) => {
        if (err?.status === 401) {
          localStorage.removeItem("access_token");
          setIsAuthorized(false);
          return;
        }
        const { errorMessage } = handleError(err);
        toast.error(errorMessage);
        console.error("Failed to accept invitation:", err);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  if (isLoading) {
    return (
      <div className="w-screen h-dvh min-h-dvh flex flex-col items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!isAuthorized) {
    return <InvitationAuthBlock />;
  }

  if (error || !invitationData) {
    return (
      <div className="w-screen h-dvh min-h-dvh flex flex-col items-center justify-center gap-4">
        <p className="text-lg font-semibold text-foreground">
          {error || "Приглашение не найдено"}
        </p>
        {error && (
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-elastic-500 hover:bg-elastic-600 text-black rounded-lg font-medium">
            Обновить страницу
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={true}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
      />
      {invitationData.invitation.type === "existing_renter" ? (
        <ExistingRenterFlow
          invitationData={invitationData}
          onAccept={handleExistingRenterAccept}
          isSubmitting={isSubmitting}
        />
      ) : (
        <NewRenterFlow
          invitationData={invitationData}
          onAccept={handleNewRenterAccept}
          isSubmitting={isSubmitting}
        />
      )}
    </>
  );
}
